// rollenRichtung.ts — wie eine ROLLENKANTE in der Firmen-Akte beschriftet wird.
//
// Struktur-Kanten des Verbunds tragen ihre Gegenrichtung im TYP und haben eine
// Typ-Inverse (Tochterfirma↔Mutterfirma, FIRMA_INVERSE in
// services/haushalteCleanupService.ts). Rollenkanten haben keine: Die Rolle steht
// in `function`, der Typ ist der Sammelwert „Mensch fürs Unternehmen". In der
// Spalte „Rolle" stand deshalb die Rolle DIESES Kontakts neben dem Namen des
// anderen — während „Mutterfirma" eine Zeile darüber den anderen meint.
//
// Statt eine Substantiv-Inverse zu erfinden („Mandant", „Auftraggeber") wird die
// RICHTUNG ausgesprochen. Genau so beschriftet das Organigramm dieselbe Kante
// seit jeher (OrganigrammPopup.edgeLabelFor: „dort Geschäftsführer") — kein neuer
// Rechtsbegriff, und Tabelle und Organigramm können nicht auseinanderlaufen.
//
// Die erfundenen Substantive wären zudem fachlich falsch: Eine KG ist nicht die
// „Auftraggeberin" ihrer Komplementär-GmbH, und ein per Beschluss bestellter
// WEG-Verwalter hat kein Auftragsverhältnis nach § 662 BGB zur Gemeinschaft —
// der Katalog führt beide als LEITUNG-Rollen. „Mandant" ist in dieser Oberfläche
// ohnehin belegt: dort heißt so der Mandant im Sinne des Tenants.
//
// Bewusst ohne React/DOM, damit die Regel per Vitest prüfbar ist — wie
// rollenEinordnung.ts.

import { roleLabelFrom } from "../../utils/derivePersonEmployment";

/**
 * Rollen-Label einer Kante aus (relationType, function).
 *
 * Anders als `roleLabelFrom` fällt eine leere `function` NICHT auf den Sammeltyp
 * zurück — „Mensch fürs Unternehmen" ist kein Rollenname, sondern die Schublade.
 * Übernommen aus `personRoleLabel` (OrganigrammPopup), das genau dafür gebaut
 * wurde und diese Funktion jetzt mitbenutzt.
 */
export function rollenLabel(relationType?: string | null, funktion?: string | null): string {
  const typ = (relationType ?? "").trim();
  const fn = (funktion ?? "").trim();
  if (typ === "Mensch fürs Unternehmen") return fn || "Verantwortlich";
  if (typ === "Mensch im Unternehmen") return "Mitarbeiter";
  return roleLabelFrom(typ, fn);
}

/** Die Felder einer Anzeigezeile, die über die Beschriftung entscheiden. */
export type RichtungsZeile = {
  relType: string;
  storedRelType?: string;
  func?: string;
  /** true = der aktuelle Kontakt ist contactDest, trägt die Rolle also selbst. */
  istEingehend?: boolean;
};

/**
 * Beschriftung einer Rollenkante in „Verbindungen zu Firmen".
 *
 * `attach-menschen` schreibt immer `src = Unternehmen`. Eine EINGEHENDE Zeile
 * meint deshalb eine Rolle DIESES Kontakts bei der anderen Firma → die Zeile
 * nennt den Ort: „dort Steuerberater". Ausgehend hält der ANDERE die Rolle hier →
 * sie steht schlicht da; so kommt auch der Rückfall bei ungeladenem Katalog
 * wortgleich beim bisherigen Verhalten heraus.
 */
export function rollenLabelMitRichtung(z: RichtungsZeile): string {
  const label = rollenLabel(z.storedRelType ?? z.relType, z.func);
  return z.istEingehend ? `dort ${label}` : label;
}
